import { useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { Plug, Wifi, Wind, Lightbulb } from "lucide-react";

/**
 * Guest-facing pod detail. Only Focus and Meet are in service, so Team is not shown.
 * Descriptors are deliberately non-technical: square footage and exterior dimensions
 * are procurement facts and live on /pods/, not here.
 * Keeps id="booth-specs" so the existing Navbar anchor still resolves.
 */

const sizes = [
  {
    key: "focus",
    name: "Focus",
    capacity: "1 person",
    headline: "For calls and heads-down work",
    description:
      "A single-person pod with a seated desk, sized for a call you cannot take on the floor or an hour of work you cannot put off.",
    points: ["Room for one, seated", "Desk for your laptop", "Made for calls and video"],
    image: "/site-images/javits-people-focus.webp",
    alt: "A professional working on a laptop inside a Quiet Corner Focus pod at the Javits Center",
  },
  {
    key: "meet",
    name: "Meet",
    capacity: "2 to 4 people",
    headline: "For meetings that need a door",
    description:
      "Seated meeting space with a table and a wall monitor, for interviews, briefings, and the conversations you would rather not have in the open.",
    points: ["Seats two to four", "Table with a wall monitor", "Made for meetings and interviews"],
    image: "/site-images/javits-people-meeting.webp",
    alt: "Two people meeting inside a Quiet Corner Meet pod at the Javits Center",
  },
];

const amenities = [
  {
    icon: Plug,
    title: "Power at the desk",
    body: "A standard outlet plus USB-C fast charging and USB-A.",
  },
  {
    icon: Wifi,
    title: "Wi-Fi throughout",
    body: "Connected the moment you sit down.",
  },
  {
    icon: Wind,
    title: "Fresh air, always moving",
    body: "A fresh air system runs continuously, so an hour inside never gets stuffy.",
  },
  {
    icon: Lightbulb,
    title: "Adjustable lighting",
    body: "Set the light level from the panel inside, so you are lit properly on a video call.",
  },
];

const InThePodSection = () => {
  const [active, setActive] = useState(sizes[0].key);
  const pod = sizes.find((s) => s.key === active) ?? sizes[0];

  return (
    <section id="booth-specs" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeading
          eyebrow="Inside the pod"
          title="Built so you can actually hear each other"
        />

        {/* Size toggle */}
        <div className="flex justify-center mb-14 -mt-4">
          <div
            role="tablist"
            aria-label="Pod sizes"
            className="inline-flex bg-secondary rounded-full p-1.5 border border-quietcove-sand/70"
          >
            {sizes.map((size) => {
              const selected = size.key === active;
              return (
                <button
                  key={size.key}
                  role="tab"
                  id={`pod-tab-${size.key}`}
                  aria-selected={selected}
                  aria-controls={`pod-panel-${size.key}`}
                  onClick={() => setActive(size.key)}
                  className={`px-7 sm:px-10 py-3 rounded-full font-display text-base font-bold transition-all duration-300 ${
                    selected
                      ? "bg-quietcove-blue text-white shadow-sm"
                      : "text-quietcove-blue/70 hover:text-quietcove-blue"
                  }`}
                >
                  {size.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active pod */}
        <Reveal
          as="div"
          role="tabpanel"
          id={`pod-panel-${pod.key}`}
          aria-labelledby={`pod-tab-${pod.key}`}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-20 md:mb-24"
        >
          <div className="rounded-2xl overflow-hidden border border-quietcove-sand/70 bg-secondary">
            <img
              key={pod.image}
              src={pod.image}
              alt={pod.alt}
              width={1000}
              height={667}
              className="w-full h-auto animate-fade-in"
            />
          </div>

          <div>
            <div className="flex items-baseline gap-3 mb-3">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-quietcove-blue">
                {pod.name}
              </h3>
              <span className="text-sm font-semibold text-gold-deep uppercase tracking-[0.18em]">
                {pod.capacity}
              </span>
            </div>

            <p className="font-display text-lg md:text-xl text-quietcove-blue/80 mb-4">
              {pod.headline}
            </p>

            <p className="text-quietcove-grey text-base md:text-lg leading-relaxed mb-6">
              {pod.description}
            </p>

            <ul className="space-y-2.5 border-t border-quietcove-sand/70 pt-5">
              {pod.points.map((point) => (
                <li key={point} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                  <span className="text-quietcove-grey">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* Shared across both sizes */}
        <div className="pt-4">
          <Reveal className="flex items-center gap-4 mb-10">
            <span className="h-px w-10 bg-gold shrink-0" />
            <h3 className="font-display text-xl font-bold tracking-[-0.015em] text-quietcove-blue">
              In every pod
            </h3>
            <span className="h-px grow bg-quietcove-blue/12" />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {amenities.map((item, i) => (
              <Reveal key={item.title} delay={i * 70}>
                <div className="border-t border-quietcove-blue/15 pt-6">
                  <item.icon className="h-6 w-6 text-gold mb-5" strokeWidth={1.5} />
                  <h4 className="font-display text-lg font-bold tracking-[-0.015em] text-quietcove-blue mb-2">
                    {item.title}
                  </h4>
                  <p className="text-quietcove-grey text-sm leading-relaxed">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InThePodSection;
